import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import CountryFlag from '@/app/component/atom/flag'
import IconText from '@/app/component/layout/icon-text'
import { OrgSubscriptionTableType } from '@/app/module/cati/types'
import { emptyFallback, formatDateTime, formatPhoneNumber, parsePhoneNumber } from '@/app/service/util/format'

type Props = {
  callContactMode?: boolean
  contact: OrgSubscriptionTableType
  timezone: string
}

const SubCard: React.FC<Props> = ({ callContactMode = false, contact, timezone }) => {
  const navigate = useNavigate()
  const { country } = parsePhoneNumber(contact.contactFullPhoneNumber)

  const goToSurvey = () => {
    const surveyPath = `/cati/${contact.campaignId}/sub/${contact.subId}`
    if (callContactMode) {
      navigate(surveyPath, { state: { autoStartCall: true } })
      return
    }

    navigate(surveyPath)
  }

  return (
    <StyledCard>
      <CardContent>
        <ContactHeader>
          <Typography variant="h6" component="div" gutterBottom>
            {emptyFallback('contact name', contact.contactName)}
          </Typography>
        </ContactHeader>

        <ContactInfo>
          <PhoneContainer>
            <CountryFlag code={country} />
            <Typography variant="body1">
              {contact.contactFullPhoneNumber
                ? formatPhoneNumber(`+${contact.contactFullPhoneNumber}`)
                : emptyFallback('phone number', contact.contactFullPhoneNumber)}
            </Typography>
          </PhoneContainer>

          <MetaInfo>
            <IconText>
              <Icon fontSize="small">language</Icon>
              <Typography component="span" variant="body2">
                {emptyFallback('language', contact.contactLanguage)}
              </Typography>
            </IconText>

            <IconText>
              <Icon fontSize="small">campaign</Icon>
              <Typography component="span" variant="body2">
                {emptyFallback('campaign', contact.campaignName)}
              </Typography>
            </IconText>

            <IconText>
              <Icon fontSize="small">calendar_today</Icon>
              <Typography component="span" variant="body2">
                Added {formatDateTime(contact.start, timezone)}
              </Typography>
            </IconText>

            <IconText>
              <Icon fontSize="small">account_circle</Icon>
              <Typography component="span" variant="body2">
                {emptyFallback('status', contact.userStatus)}
              </Typography>
            </IconText>
          </MetaInfo>
        </ContactInfo>
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={goToSurvey}
          endIcon={!callContactMode ? <Icon fontSize="small">chevron_right</Icon> : undefined}
          startIcon={callContactMode ? <Icon fontSize="small">call</Icon> : undefined}
        >
          {callContactMode ? 'Call Contact' : 'Go to survey'}
        </Button>
      </CardActions>
    </StyledCard>
  )
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:hover': {
    boxShadow: theme.shadows[4],
    cursor: 'pointer',
  },
}))

const ContactInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const ContactHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
})

const PhoneContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}))

const MetaInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}))

export default SubCard
